// Lightweight global hotkey hook. Skips when focus is in an input/textarea
// unless `allowInInput` is set — the NL search input opts in for `mod+k`.
import { useEffect } from "react";

export interface HotkeyOptions {
  allowInInput?: boolean;
  preventDefault?: boolean;
}

function matches(e: KeyboardEvent, combo: string): boolean {
  // Combos: "mod+k", "escape", "1".
  const parts = combo.toLowerCase().split("+");
  const key = parts[parts.length - 1];
  const wantMod = parts.includes("mod") || parts.includes("cmd") || parts.includes("ctrl");
  const isMod = e.metaKey || e.ctrlKey;
  if (wantMod && !isMod) return false;
  if (!wantMod && isMod) return false;
  return e.key.toLowerCase() === key;
}

export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  opts: HotkeyOptions = {},
): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (inField && !opts.allowInInput) return;
      if (!matches(e, combo)) return;
      if (opts.preventDefault) e.preventDefault();
      handler(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [combo, handler, opts.allowInInput, opts.preventDefault]);
}
